/**
 * « Exporter en notes » — fonctions PURES : depuis les résultats d'une
 * évaluation par maîtrise (eleves_resultats du GET evaluations/{id}/),
 * produire la note de chaque élève sur un barème déclaré, et les formats
 * de sortie (TSV presse-papier « Élèves ⇥ Notes », CSV Excel FR).
 *
 * Règles actées (2026-08-30) :
 *   - note = moyenne des cases ÉVALUÉES (scores 1..6 ; null/0 exclus,
 *     comme le calcul d'acquisition) ÷ 6 × barème ;
 *   - arrondi au DEMI-POINT ;
 *   - élève sans aucune case évaluée → ligne présente, note vide ;
 *   - décimales à VIRGULE (public français) dans les deux sorties.
 */

import { identiteEleve } from './collerNotes';

/** Moyenne des scores 1..6 convertie sur le barème, arrondie au demi-point.
 *  null si aucune case évaluée ou barème invalide. */
export const noteDepuisScores = (scores, bareme) => {
    if (!(bareme > 0)) return null;
    const evalues = (scores || []).filter((s) => typeof s === 'number' && s >= 1 && s <= 6);
    if (!evalues.length) return null;
    const moyenne = evalues.reduce((somme, s) => somme + s, 0) / evalues.length;
    return Math.round((moyenne / 6) * bareme * 2) / 2;
};

/** Une ligne par élève : { nom: 'NOM Prénom' (priorité GAR), note|null },
 *  triées par nom. */
export const lignesNotesDepuisResultat = (elevesResultats, bareme) =>
    (elevesResultats || [])
        .map((eleve) => ({
            nom: identiteEleve(eleve),
            note: noteDepuisScores((eleve.resultats || []).map((r) => r.scores), bareme),
        }))
        .sort((a, b) => a.nom.localeCompare(b.nom));

/** Nombre au format français (virgule décimale), '' si null. */
const nombreFr = (n) => (n == null ? '' : String(n).replace('.', ','));

/** TSV presse-papier : collable tel quel dans Excel / le logiciel de notes. */
export const tsvNotes = (lignes) => ['Élèves\tNotes',
    ...(lignes || []).map((l) => `${l.nom}\t${nombreFr(l.note)}`),
].join('\n');

// ─── Export unifié (Notes / Par compétence / Les deux) ──────────────────

/** En-têtes des colonnes de compétences, comme l'export historique :
 *  « <attendu ou savoir-faire> : <compétence> ». */
const entetesCompetences = (elevesResultats) =>
    ((elevesResultats || [])[0]?.resultats || []).map((r) =>
        `${r.savoirTitre ? r.savoirTitre : r.attenduTitre} : ${r.competenceNom}`);

/**
 * Tableau (array of arrays) du contenu choisi :
 *   - 'notes'       → Élèves | Notes (converties sur le barème) ;
 *   - 'competences' → Élèves | une colonne par attendu × compétence (scores
 *                     bruts 1..6, vide si non évalué) ;
 *   - 'deux'        → Élèves | Notes | colonnes de compétences.
 * L'ordre des élèves est alphabétique (identité GAR prioritaire).
 */
export const aoaExport = (elevesResultats, bareme, contenu = 'notes') => {
    const avecNotes = contenu !== 'competences';
    const avecComps = contenu !== 'notes';
    const tries = [...(elevesResultats || [])]
        .sort((a, b) => identiteEleve(a).localeCompare(identiteEleve(b)));

    const entetes = ['Élèves'];
    if (avecNotes) entetes.push('Notes');
    if (avecComps) entetes.push(...entetesCompetences(elevesResultats));

    const lignes = tries.map((eleve) => {
        const scores = (eleve.resultats || []).map((r) => r.scores);
        const ligne = [identiteEleve(eleve)];
        if (avecNotes) ligne.push(noteDepuisScores(scores, bareme));
        if (avecComps) {
            ligne.push(...scores.map((s) => (typeof s === 'number' && s >= 1 && s <= 6 ? s : null)));
        }
        return ligne;
    });
    return [entetes, ...lignes];
};

/** TSV presse-papier d'un tableau d'export (décimales à virgule, cellules
 *  vides pour les null). */
export const tsvDepuisAoa = (aoa) => (aoa || [])
    .map((ligne) => ligne.map((c) => (typeof c === 'number' ? nombreFr(c) : (c ?? ''))).join('\t'))
    .join('\n');

/** Statistiques d'aperçu du popover. */
export const resumeNotes = (lignes) => {
    const notees = (lignes || []).filter((l) => l.note != null);
    const moyenne = notees.length
        ? Math.round((notees.reduce((s, l) => s + l.note, 0) / notees.length) * 10) / 10
        : null;
    return { nbNotes: notees.length, nbSansNote: (lignes || []).length - notees.length, moyenne };
};
